import {
  ActivityTableColumnPreference,
  createFormColumnId,
  sanitizeActivityTableLabel,
  normalizeActivityTableLabel,
  normalizeActivityTableAsciiLabel,
} from "../../utils/activityTableColumns";
import { ProjectFormElement } from "../../types";

export const PROJECT_TABLE_SCREEN_KEY = "manager_dashboard_projects" as const;

export type ProjectFormColumnValueType =
  | "text"
  | "number"
  | "date"
  | "select"
  | "boolean";

export type ProjectTableColumnPreference = ActivityTableColumnPreference & {
  projectElementType?: ProjectFormElement["type"];
  projectValueType?: ProjectFormColumnValueType;
};

const CANONICAL_PROJECT_M2_LABEL = "İnşaat m^2";

const inferProjectFormColumnValueTypeFromLabel = (
  label?: string | null,
): ProjectFormColumnValueType => {
  const normalizedLabel = normalizeProjectFormColumnSemanticLabel(label);

  if (!normalizedLabel) {
    return "text";
  }

  if (normalizedLabel === "insaat m 2") {
    return "number";
  }

  return "text";
};

export const normalizeProjectFormColumnSemanticLabel = (
  label?: string | null,
): string => {
  const normalizedLabel = normalizeActivityTableLabel(label);
  const asciiLabel = normalizeActivityTableAsciiLabel(label);

  if (
    normalizedLabel === "m2" ||
    normalizedLabel === "m 2" ||
    normalizedLabel === "insaat m 2" ||
    asciiLabel === "insaat m 2"
  ) {
    return "insaat m 2";
  }

  return normalizedLabel;
};

export const getCanonicalProjectFormColumnLabel = (
  label?: string | null,
): string => {
  const safeLabel = sanitizeActivityTableLabel(label).trim();

  if (!safeLabel) {
    return "";
  }

  return normalizeProjectFormColumnSemanticLabel(safeLabel) === "insaat m 2"
    ? CANONICAL_PROJECT_M2_LABEL
    : safeLabel;
};

export const createProjectFormColumnPreference = (
  label: string,
  overrides: Partial<ProjectTableColumnPreference> = {},
): ProjectTableColumnPreference => {
  const safeLabel = getCanonicalProjectFormColumnLabel(label);
  const inferredValueType = inferProjectFormColumnValueTypeFromLabel(safeLabel);
  const inferredElementType: ProjectFormElement["type"] =
    inferredValueType === "number" ? "number" : "text";

  return {
    id: createFormColumnId(safeLabel),
    kind: "form",
    label: safeLabel,
    projectElementType: inferredElementType,
    projectValueType: inferredValueType,
    ...overrides,
  };
};

type ProjectFormColumnItem = {
  formData: Record<string, any>;
  containerFormElements: ProjectFormElement[];
};

type ProjectFormColumnMatch = {
  element: ProjectFormElement;
  rawValue: unknown;
  formattedValue: string;
};

export const PROJECT_NUMERIC_FILTER_KEY_BY_COLUMN_ID: Record<string, string> = {
  spentHours: "minDurationHours",
  projectAmount: "minProjectAmount",
  invoiceTotal: "minInvoiceTotal",
  totalCollected: "minCollectedAmount",
  remainingCollection: "minRemainingCollection",
  realizedCost: "minRealizedCost",
  remainingEstimatedCost: "minRemainingEstimatedCost",
  totalEstimatedCost: "minTotalEstimatedCost",
  netProfit: "minNetProfit",
  estimatedNetProfit: "minEstimatedNetProfit",
};

export const PROJECT_SORT_FIELD_BY_COLUMN_ID: Record<string, string> = {
  projectName: "workflowContainerName",
  workflowName: "templateWorkflowName",
  managerName: "projectManager",
  spentHours: "totalSpentHours",
};

const EXCLUDED_PROJECT_FORM_FIELD_IDS = new Set([
  "name",
  "projectName",
  "clientName",
  "client",
  "projectManager",
  "project_manager",
  "startDate",
  "endDate",
  "estimatedDurationDays",
  "estimatedDurationHours",
  "projectCurrency",
  "currency",
  "currencyCode",
  "offer_amount",
  "offerAmount",
  "invoice",
  "fatura",
]);

const EXCLUDED_PROJECT_FORM_FIELD_TYPES = new Set([
  "heading",
  "offer_amount",
  "invoice",
  "project_duration",
]);

const PROJECT_FORM_NUMBER_TYPES = new Set<ProjectFormElement["type"]>([
  "number",
  "budget_input",
  "currency_input",
]);

const PROJECT_FORM_SELECT_TYPES = new Set<ProjectFormElement["type"]>([
  "select",
  "project_type_select",
  "team_member_select",
  "client_select",
  "country_select",
  "city_select",
  "employee_select",
  "province_select",
  "district_select",
  "neighborhood_select",
]);

const hasProjectFormValue = (
  element: ProjectFormElement,
  rawValue: unknown,
): boolean => {
  if (rawValue === null || rawValue === undefined) {
    return false;
  }

  if (element.type === "checkbox") {
    return true;
  }

  if (Array.isArray(rawValue)) {
    return rawValue.length > 0;
  }

  return rawValue !== "";
};

const normalizeNumericString = (value: string): string => {
  const cleaned = value.replace(/[^\d,.-]/g, "").trim();
  if (!cleaned) {
    return "";
  }

  const hasComma = cleaned.includes(",");
  const hasDot = cleaned.includes(".");

  if (hasComma && hasDot) {
    return cleaned.lastIndexOf(",") > cleaned.lastIndexOf(".")
      ? cleaned.replace(/\./g, "").replace(",", ".")
      : cleaned.replace(/,/g, "");
  }

  if (hasComma) {
    return /,\d{1,2}$/.test(cleaned)
      ? cleaned.replace(/\./g, "").replace(",", ".")
      : cleaned.replace(/,/g, "");
  }

  return cleaned;
};

export const isNumericProjectColumn = (
  column: ActivityTableColumnPreference | ProjectTableColumnPreference,
): boolean =>
  column.kind === "form"
    ? getProjectFormColumnValueType(column as ProjectTableColumnPreference) ===
      "number"
    : column.id in PROJECT_NUMERIC_FILTER_KEY_BY_COLUMN_ID ||
      column.id === "plannedHours" ||
      column.id === "remainingHours" ||
      column.id === "vatAmount";

export const parseProjectFormData = (project: any): Record<string, any> => {
  const rawValue = project?.workflowContainerFormData;

  if (!rawValue) {
    return {};
  }

  if (typeof rawValue === "string") {
    try {
      return JSON.parse(rawValue);
    } catch {
      return {};
    }
  }

  if (typeof rawValue === "object") {
    return rawValue as Record<string, any>;
  }

  return {};
};

export const getProjectContainerFormElements = (
  project: any,
): ProjectFormElement[] => {
  return Array.isArray(project?.workflowContainerFormElements)
    ? project.workflowContainerFormElements
    : [];
};

export const isSelectableProjectFormElement = (
  element?: ProjectFormElement | null,
): boolean => {
  if (!element?.id || !element?.label) {
    return false;
  }

  if (EXCLUDED_PROJECT_FORM_FIELD_IDS.has(element.id)) {
    return false;
  }

  if (EXCLUDED_PROJECT_FORM_FIELD_TYPES.has(element.type)) {
    return false;
  }

  return sanitizeActivityTableLabel(element.label).trim().length > 0;
};

export const getProjectFormColumnValueTypeFromElement = (
  element?: ProjectFormElement | null,
): ProjectFormColumnValueType => {
  if (!element) {
    return "text";
  }

  if (PROJECT_FORM_NUMBER_TYPES.has(element.type)) {
    return "number";
  }

  if (element.type === "date") {
    return "date";
  }

  if (PROJECT_FORM_SELECT_TYPES.has(element.type)) {
    return "select";
  }

  if (element.type === "checkbox") {
    return "boolean";
  }

  return "text";
};

export const getProjectFormColumnValueType = (
  column?: ProjectTableColumnPreference | null,
): ProjectFormColumnValueType =>
  column?.kind === "form" ? column.projectValueType || "text" : "text";

export const collectProjectFormColumns = (
  projects: any[],
): ProjectTableColumnPreference[] => {
  const byId = new Map<
    string,
    { column: ProjectTableColumnPreference; priority: number; order: number }
  >();

  projects.forEach((project) => {
    getProjectContainerFormElements(project)
      .filter(isSelectableProjectFormElement)
      .forEach((element) => {
        const label = getCanonicalProjectFormColumnLabel(element.label);
        if (!label) {
          return;
        }

        const id = createFormColumnId(label);
        const priority = element.showInList ? 0 : 1;
        const order = Number.isFinite(element.listColumnOrder)
          ? Number(element.listColumnOrder)
          : Number.MAX_SAFE_INTEGER;
        const existing = byId.get(id);

        if (
          !existing ||
          priority < existing.priority ||
          (priority === existing.priority && order < existing.order)
        ) {
          byId.set(id, {
            column: {
              id,
              kind: "form",
              label,
              projectElementType: element.type,
              projectValueType: getProjectFormColumnValueTypeFromElement(element),
            },
            priority,
            order,
          });
        }
      });
  });

  return Array.from(byId.values())
    .sort(
      (a, b) =>
        a.priority - b.priority ||
        a.order - b.order ||
        a.column.label.localeCompare(b.column.label, "tr-TR"),
    )
    .map((item) => item.column);
};

export const formatProjectFormPrimitive = (
  value: unknown,
  t: (key: string, options?: any) => string,
): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => formatProjectFormPrimitive(item, t))
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "boolean") {
    return value
      ? t("common:yes", { defaultValue: "Evet" })
      : t("common:no", { defaultValue: "Hayır" });
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;

    if (record.firstName || record.lastName) {
      return [record.firstName, record.lastName]
        .filter(Boolean)
        .map((item) => String(item).trim())
        .join(" ");
    }

    const preferred =
      record.label ?? record.name ?? record.title ?? record.value ?? null;

    if (preferred !== null && preferred !== undefined && preferred !== value) {
      return formatProjectFormPrimitive(preferred, t);
    }

    try {
      return JSON.stringify(record);
    } catch {
      return "";
    }
  }

  return String(value).trim();
};

export const formatProjectFormElementValue = (
  element: ProjectFormElement,
  rawValue: unknown,
  t: (key: string, options?: any) => string,
): string => {
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return "";
  }

  if (element.type === "date" && typeof rawValue === "string") {
    const parsed = new Date(rawValue);
    return Number.isNaN(parsed.getTime())
      ? rawValue
      : parsed.toLocaleDateString("tr-TR");
  }

  if (element.type === "date_range" && typeof rawValue === "object") {
    const range = rawValue as Record<string, unknown>;
    const start = formatProjectFormPrimitive(
      range.startDate ?? range.start,
      t,
    );
    const end = formatProjectFormPrimitive(range.endDate ?? range.end, t);
    return [start, end].filter(Boolean).join(" - ");
  }

  if (element.type === "checkbox") {
    return formatProjectFormPrimitive(Boolean(rawValue), t);
  }

  return formatProjectFormPrimitive(rawValue, t);
};

const getProjectFormColumnMatches = (
  item: ProjectFormColumnItem,
  column: ActivityTableColumnPreference | ProjectTableColumnPreference,
  t: (key: string, options?: any) => string,
): ProjectFormColumnMatch[] => {
  if (column.kind !== "form") {
    return [];
  }

  const normalizedTarget =
    normalizeProjectFormColumnSemanticLabel(column.label) ||
    (column.id.startsWith("form:")
      ? normalizeProjectFormColumnSemanticLabel(column.id.slice(5).trim())
      : "");

  if (!normalizedTarget) {
    return [];
  }

  return (item.containerFormElements || [])
    .filter(isSelectableProjectFormElement)
    .filter(
      (element) =>
        normalizeProjectFormColumnSemanticLabel(element.label) === normalizedTarget,
    )
    .filter((element) => hasProjectFormValue(element, item.formData?.[element.id]))
    .map((element) => {
      const rawValue = item.formData?.[element.id];
      return {
        element,
        rawValue,
        formattedValue: formatProjectFormElementValue(element, rawValue, t),
      };
    })
    .filter((match) => Boolean(match.formattedValue));
};

export const parseProjectFormNumericValue = (
  rawValue: unknown,
): number | null => {
  if (typeof rawValue === "number") {
    return Number.isFinite(rawValue) ? rawValue : null;
  }

  if (typeof rawValue === "string") {
    const normalized = normalizeNumericString(rawValue);
    if (!normalized) {
      return null;
    }
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (Array.isArray(rawValue) || typeof rawValue === "boolean") {
    return null;
  }

  if (rawValue && typeof rawValue === "object") {
    const record = rawValue as Record<string, unknown>;
    return (
      parseProjectFormNumericValue(record.value) ??
      parseProjectFormNumericValue(record.amount) ??
      parseProjectFormNumericValue(record.number)
    );
  }

  return null;
};

export const getProjectFormColumnNumericValue = (
  item: ProjectFormColumnItem,
  column: ActivityTableColumnPreference | ProjectTableColumnPreference,
  t: (key: string, options?: any) => string,
): number | null => {
  const match = getProjectFormColumnMatches(item, column, t).find((entry) => {
    const rawNumeric = parseProjectFormNumericValue(entry.rawValue);
    const formattedNumeric = parseProjectFormNumericValue(entry.formattedValue);
    return rawNumeric !== null || formattedNumeric !== null;
  });

  if (!match) {
    return null;
  }

  return (
    parseProjectFormNumericValue(match.rawValue) ??
    parseProjectFormNumericValue(match.formattedValue)
  );
};

export const getProjectFormColumnDateValue = (
  item: ProjectFormColumnItem,
  column: ActivityTableColumnPreference | ProjectTableColumnPreference,
  t: (key: string, options?: any) => string,
): number | null => {
  const match = getProjectFormColumnMatches(item, column, t).find((entry) => {
    if (entry.element.type === "date" && typeof entry.rawValue === "string") {
      return true;
    }

    const rawValue =
      entry.rawValue && typeof entry.rawValue === "object"
        ? ((entry.rawValue as Record<string, unknown>).startDate ??
            (entry.rawValue as Record<string, unknown>).start) as
            | string
            | undefined
        : undefined;

    return typeof rawValue === "string";
  });

  if (!match) {
    return null;
  }

  const rawDateValue =
    match.element.type === "date"
      ? match.rawValue
      : match.rawValue && typeof match.rawValue === "object"
        ? (match.rawValue as Record<string, unknown>).startDate ??
          (match.rawValue as Record<string, unknown>).start
        : null;

  if (typeof rawDateValue !== "string") {
    return null;
  }

  const parsed = new Date(rawDateValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

export const getProjectFormColumnFilterOptions = (
  items: ProjectFormColumnItem[],
  column: ActivityTableColumnPreference | ProjectTableColumnPreference,
  t: (key: string, options?: any) => string,
): string[] =>
  Array.from(
    new Set(
      items
        .map((item) => getProjectFormColumnValue(item, column, t))
        .filter((value) => Boolean(value && value !== "-")),
    ),
  ).sort((a, b) => a.localeCompare(b, "tr-TR"));

export const getProjectFormColumnValue = (
  item: ProjectFormColumnItem,
  column: ActivityTableColumnPreference | ProjectTableColumnPreference,
  t: (key: string, options?: any) => string,
): string => {
  const values = getProjectFormColumnMatches(item, column, t).map(
    (match) => match.formattedValue,
  );

  return Array.from(new Set(values)).join(" | ");
};

export const formatProjectDate = (value?: string | null): string => {
  if (!value || typeof value !== "string") {
    return "-";
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString("tr-TR", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
};
